import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_LOW,
    PASTE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';
import { $generateNodesFromMarkdown } from '../utils/serialization';
import { $isQuoteNode } from '@lexical/rich-text';
import { $isWhatsAppQuoteNode } from '../nodes';

export function WhatsAppPastePlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            PASTE_COMMAND,
            (event: ClipboardEvent) => {
                const clipboardData = event.clipboardData;
                if (!clipboardData) return false;

                // Prioritize HTML if available for rich text
                const htmlContent = clipboardData.getData('text/html');
                if (htmlContent) {
                    event.preventDefault();
                    const markdown = htmlToWhatsAppMarkdown(htmlContent);
                    editor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            const nodes = $generateNodesFromMarkdown(markdown);
                            
                            // Check if we're pasting into a quote
                            const anchorNode = selection.anchor.getNode();
                            let currentNode = anchorNode;
                            let inQuote = false;
                            
                            while (currentNode) {
                                if ($isQuoteNode(currentNode) || $isWhatsAppQuoteNode(currentNode)) {
                                    inQuote = true;
                                    break;
                                }
                                const parent = currentNode.getParent();
                                if (!parent || parent.getType() === 'root') break;
                                currentNode = parent;
                            }
                            
                            if (inQuote && nodes.length > 1) {
                                // Pasting into a quote with multiple nodes
                                // Insert first node inline, rest after the quote
                                const quoteNode = currentNode;
                                selection.insertNodes([nodes[0]]);
                                
                                // Insert remaining nodes after the quote, maintaining order
                                let lastNode: any = quoteNode;
                                for (let i = 1; i < nodes.length; i++) {
                                    lastNode.insertAfter(nodes[i]);
                                    lastNode = nodes[i] as any;
                                }
                            } else {
                                // Normal paste
                                selection.insertNodes(nodes);
                            }
                        }
                    });
                    return true;
                }

                const textContent = clipboardData.getData('text/plain');
                if (textContent) {
                    event.preventDefault();
                    editor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            const nodes = $generateNodesFromMarkdown(textContent);
                            
                            // Check if we're pasting into a quote
                            const anchorNode = selection.anchor.getNode();
                            let currentNode = anchorNode;
                            let inQuote = false;
                            
                            while (currentNode) {
                                if ($isQuoteNode(currentNode) || $isWhatsAppQuoteNode(currentNode)) {
                                    inQuote = true;
                                    break;
                                }
                                const parent = currentNode.getParent();
                                if (!parent || parent.getType() === 'root') break;
                                currentNode = parent;
                            }
                            
                            if (inQuote && nodes.length > 1) {
                                // Pasting into a quote with multiple nodes
                                // Insert first node inline, rest after the quote
                                const quoteNode = currentNode;
                                selection.insertNodes([nodes[0]]);
                                
                                // Insert remaining nodes after the quote, maintaining order
                                let lastNode: any = quoteNode;
                                for (let i = 1; i < nodes.length; i++) {
                                    lastNode.insertAfter(nodes[i]);
                                    lastNode = nodes[i] as any;
                                }
                            } else {
                                // Normal paste
                                selection.insertNodes(nodes);
                            }
                        }
                    });
                    return true;
                }

                return false;
            },
            COMMAND_PRIORITY_LOW, 
        );
    }, [editor]);

    return null;
}

/**
 * Basic HTML to WhatsApp Markdown converter
 * Preserves simple formatting: b, i, s, code, pre
 * Converts block elements to newlines
 */
export function htmlToWhatsAppMarkdown(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    interface FormatContext {
        bold: boolean;
        italic: boolean;
        strike: boolean;
    }

    // Helper to traverse and build markdown
    function traverse(node: Node, context: FormatContext = { bold: false, italic: false, strike: false }): string {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent || '';
        }
        
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return '';
        }
        
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();

        // Handle img tags - extract alt text (used by Telegram and other sites for emojis)
        if (tag === 'img') {
            const alt = el.getAttribute('alt');
            if (alt) {
                return alt;
            }
            return '';
        }

        let content = '';
        const style = el.style as any || {};
        
        // Determine new formats
        // IMPORTANT: Inline styles should override tag names
        // For example, <b style="font-weight:normal"> should NOT be bold
        
        // Check if element has explicit style values
        const hasExplicitBoldStyle = style.fontWeight !== undefined && style.fontWeight !== '';
        const hasExplicitItalicStyle = style.fontStyle !== undefined && style.fontStyle !== '';
        const hasExplicitStrikeStyle = style.textDecoration !== undefined && style.textDecoration !== '';
        
        // Determine if bold based on style first, then tag
        let isBoldRaw: boolean;
        if (hasExplicitBoldStyle) {
            // If explicit style exists, use it (font-weight: 700+ or 'bold' = bold, anything else = not bold)
            isBoldRaw = style.fontWeight === 'bold' || parseInt(style.fontWeight || '0') >= 700;
        } else {
            // No explicit style, check tag name
            isBoldRaw = tag === 'b' || tag === 'strong';
        }
        
        // Determine if italic based on style first, then tag
        let isItalicRaw: boolean;
        if (hasExplicitItalicStyle) {
            isItalicRaw = style.fontStyle === 'italic';
        } else {
            isItalicRaw = tag === 'i' || tag === 'em';
        }
        
        // Determine if strikethrough based on style first, then tag
        let isStrikeRaw: boolean;
        if (hasExplicitStrikeStyle) {
            isStrikeRaw = (style.textDecoration || '').includes('line-through');
        } else {
            isStrikeRaw = tag === 's' || tag === 'strike' || tag === 'del';
        }
        
        // Only trigger formatting if it's a NEW application of the style
        const applyBold = isBoldRaw && !context.bold;
        const applyItalic = isItalicRaw && !context.italic;
        const applyStrike = isStrikeRaw && !context.strike;


        // pass updated context to children
        const nextContext = {
            bold: context.bold || applyBold,
            italic: context.italic || applyItalic,
            strike: context.strike || applyStrike
        };
        
        const isBlockTag = (t: string) => ['p', 'div', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(t);
        const isBlockNode = (n: Node) => n.nodeType === Node.ELEMENT_NODE && isBlockTag((n as HTMLElement).tagName.toLowerCase());

        // Process children
        for (let i = 0; i < el.childNodes.length; i++) {
            const child = el.childNodes[i];
            
            // Check for skippable whitespace: 
            // Text node, purely whitespace, bordered by a block element?
            if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
                const prev = el.childNodes[i - 1];
                const next = el.childNodes[i + 1];
                
                // If either neighbor is a block, this whitespace is likely structural formatting -> skip it
                if ((prev && isBlockNode(prev)) || (next && isBlockNode(next))) {
                    continue;
                }
            }
            
            content += traverse(child, nextContext);
        }
        
        const isCode = tag === 'code';
        const isPre = tag === 'pre';
        
        // Check if code element has display: block style (makes it a code block)
        const isBlockCode = isCode && (style.display === 'block' || el.parentElement?.tagName.toLowerCase() === 'pre');
        
        if (isPre || isBlockCode) {
             // Block code
            return `\`\`\`\n${content}\n\`\`\``;
        }

        if (isCode) {
            // Inline code
            return `\`${content}\``;
        }

        // Helper to apply formatting to content, handling multiline (soft breaks)
        const applyFormat = (text: string, char: string): string => {
            if (!text.includes('\n')) {
                return `${char}${text}${char}`;
            }
            return text
                .split('\n')
                .map(line => {
                    if (!line.trim()) return line;
                    return `${char}${line}${char}`;
                })
                .join('\n');
        };

        if (applyBold) content = applyFormat(content, '*');
        if (applyItalic) content = applyFormat(content, '_');
        if (applyStrike) content = applyFormat(content, '~');
        
        if (tag === 'br') return '\n';
        
        if (tag === 'li') {
            // Determine if this is in an ordered or unordered list
            const parentTag = el.parentElement?.tagName.toLowerCase();
            let marker = '';
            
            if (parentTag === 'ol') {
                // For ordered lists, use the value attribute or calculate position
                const value = el.getAttribute('value');
                marker = value ? `${value}. ` : '1. ';
            } else if (parentTag === 'ul') {
                // For unordered lists, use bullet
                marker = '• ';
            }
            
            return `\n${marker}${content}`;
        }
        
        if (tag === 'p' || tag === 'div') {
             return `\n${content}`;
        }

        
        if (tag === 'ul' || tag === 'ol') {
             // List containers: just return the content from li elements
             // Li elements already handle their own newlines
             return content;
        }

        if (tag === 'blockquote') {
            // Convert blockquote to WhatsApp quote format
            // Each line in the blockquote should be prefixed with >
            return content
                .split('\n')
                .filter(line => line.trim()) // Remove empty lines
                .map(line => `\n> ${line}`)
                .join('');
        }

        const isBlock = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag);

        if (isBlock) {
             return `\n${content}\n`;
        }
        
        return content;
    }
    
    // Process body
    const result = traverse(doc.body);
    
    // Cleanup extra newlines (more than 2)
    return result.replace(/\n{3,}/g, '\n\n').trim();
}
